// 📄 components/shared/PlantillaMetadatosForm.tsx
// Metadatos de trazabilidad CIG de una plantilla del Validador de
// Estructura — compartido entre PlantillaGlobalFormModal.tsx (Admin),
// EditarPlantillaProyectoModal.tsx y CargarPlantillaModal.tsx (proyecto),
// mismo criterio que RestriccionesTagEditor.tsx: un solo componente
// controlado en los tres, no se triplica el formulario. Todos los campos
// son opcionales — plantillas guardadas antes de que existiera este
// formulario simplemente no los tienen (ver leerPlantilla en
// plantillasReferenciaService.js).
import { PlantillaMetadatos } from "../../services/plantillaReferenciaService.ts";

interface PlantillaMetadatosFormProps {
  valores: PlantillaMetadatos;
  onChange: (valores: PlantillaMetadatos) => void;
}

// Sugerencias del datalist — texto libre igual, esto es solo para
// autocompletar los valores típicos de CIG sin forzar un enum.
const SUGERENCIAS_DOCUMENT_TYPE = [
  "PurchaseOrderExportRequest",
  "ContractRequest",
  "InvoiceRequest",
  "OrderConfirmationRequest",
  "ServiceEntrySheetRequest",
  "SupplierDataRequest",
  "GoodsReceiptRequest",
  "Otro"
];

const DOCUMENT_STAGES = ["Inbound", "PreProcess", "Process", "Extension", "Routing", "Outbound"];
const DOCUMENT_STANDARDS = ["cXML", "AddOn", "IDoc", "SOAP", "Otro"];

export default function PlantillaMetadatosForm({ valores, onChange }: PlantillaMetadatosFormProps) {
  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  const update = (campo: keyof PlantillaMetadatos, valor: string) => {
    onChange({ ...valores, [campo]: valor });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={labelClass}>Document Type (CIG)</label>
        <input
          type="text"
          list="onehub-document-type-sugerencias"
          className={inputClass}
          value={valores.documentType || ""}
          onChange={(e) => update("documentType", e.target.value)}
          placeholder="ej. PurchaseOrderExportRequest"
        />
        <datalist id="onehub-document-type-sugerencias">
          {SUGERENCIAS_DOCUMENT_TYPE.map((s) => <option key={s} value={s} />)}
        </datalist>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Document Stage</label>
          <select className={inputClass} value={valores.documentStage || ""} onChange={(e) => update("documentStage", e.target.value)}>
            <option value="">Sin especificar</option>
            {DOCUMENT_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Estándar del documento</label>
          <select className={inputClass} value={valores.documentStandard || ""} onChange={(e) => update("documentStandard", e.target.value)}>
            <option value="">Sin especificar</option>
            {DOCUMENT_STANDARDS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Sistema origen (Sender)</label>
          <input
            type="text"
            className={inputClass}
            value={valores.sistemaOrigen || ""}
            onChange={(e) => update("sistemaOrigen", e.target.value)}
            placeholder="ej. realm-Child1-T, Ariba Buying"
          />
        </div>
        <div>
          <label className={labelClass}>Sistema destino (Receiver)</label>
          <input
            type="text"
            className={inputClass}
            value={valores.sistemaDestino || ""}
            onChange={(e) => update("sistemaDestino", e.target.value)}
            placeholder="ej. SAPCLNT300, SAP ERP"
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Descripción / Contexto</label>
        <textarea
          className={`${inputClass} resize-y`}
          rows={2}
          value={valores.descripcion || ""}
          onChange={(e) => update("descripcion", e.target.value)}
          placeholder="ej. XML de OC creada desde Buying en QAS, procesado correctamente en PreProcess el 04/08/2026."
        />
      </div>
    </div>
  );
}
